import { apiClient } from "@/api/api-client";
import { Button } from "@/components/core/buttons/button";
import { Input } from "@/components/core/forms/input";
import { useValidationErrors } from "@/hooks/use-validation-errors";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/register")({
	component: RegisterPage,
});

function RegisterPage() {
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const { errors, setErrors, clearErrors } = useValidationErrors();
	const [showSuccess, setShowSuccess] = useState(false);

	const [formData, setFormData] = useState({
		first_name: "",
		last_name: "",
		email: "",
		password: "",
		password_confirmation: "",
	});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		clearErrors();

		// Client-side validation for @revlv.com domain
		if (!formData.email.endsWith("@revlv.com")) {
			setErrors({ email: ["Email must be from @revlv.com domain"] });
			toast.error("Only @revlv.com email addresses are allowed.");
			return;
		}

		setIsLoading(true);

		try {
			await apiClient.post("/register", formData);
			setShowSuccess(true);
			toast.success("Registration successful! Please wait for admin approval.");
		} catch (error: unknown) {
			const axiosError = error as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } };
			if (axiosError?.response?.status === 422) {
				setErrors(axiosError.response.data?.errors || {});
				toast.error("Please check the form and try again.");
			} else {
				toast.error("An error occurred. Please try again.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		// Clear error for this field when user types
		if (errors[name]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[name];
				return newErrors;
			});
		}
	};

	if (showSuccess) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-slate-800 py-12 px-4 sm:px-6 lg:px-8">
				<div className="max-w-md w-full space-y-8">
					<div className="bg-white rounded-lg p-8 text-center">
						<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
							<svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" role="img" aria-label="Success checkmark">
								<title>Success checkmark</title>
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
							</svg>
						</div>
						<h3 className="text-lg font-medium text-gray-900 mb-2">Registration Submitted</h3>
						<p className="text-sm text-gray-500 mb-6">
							Your account has been created and is pending approval from an administrator. You will receive an email notification once your account is approved.
						</p>
						<Link to="/login">
							<Button variant="outline" className="w-full">
								Back to Login
							</Button>
						</Link>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full space-y-8">
				<div>
					<div className="mx-auto rounded-lg flex items-center justify-center p-6">
						<img className="h-full w-full object-contain" src="/salesmon-logo.png" alt="Salesmon" />
					</div>
					<h2 className="text-center text-3xl font-extrabold">Create your account</h2>
					<p className="mt-2 text-center text-sm">Only @revlv.com email addresses are allowed</p>
				</div>

				<form className="mt-8 space-y-6" onSubmit={handleSubmit}>
					<div className="rounded-md shadow-sm space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label htmlFor="first_name" className="sr-only">
									First Name
								</label>
								<Input
									id="first_name"
									name="first_name"
									type="text"
									autoComplete="given-name"
									required
									className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
									placeholder="First Name"
									value={formData.first_name}
									onChange={handleInputChange}
									disabled={isLoading}
								/>
								{errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name[0]}</p>}
							</div>

							<div>
								<label htmlFor="last_name" className="sr-only">
									Last Name
								</label>
								<Input
									id="last_name"
									name="last_name"
									type="text"
									autoComplete="family-name"
									required
									className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
									placeholder="Last Name"
									value={formData.last_name}
									onChange={handleInputChange}
									disabled={isLoading}
								/>
								{errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name[0]}</p>}
							</div>
						</div>

						<div>
							<label htmlFor="email" className="sr-only">
								Email address
							</label>
							<Input
								id="email"
								name="email"
								type="email"
								autoComplete="email"
								required
								className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
								placeholder="Email address (must be @revlv.com)"
								value={formData.email}
								onChange={handleInputChange}
								disabled={isLoading}
							/>
							{errors.email && <p className="mt-1 text-sm text-red-600">{errors.email[0]}</p>}
						</div>

						<div>
							<label htmlFor="password" className="sr-only">
								Password
							</label>
							<Input
								id="password"
								name="password"
								type="password"
								autoComplete="new-password"
								required
								className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
								placeholder="Password"
								value={formData.password}
								onChange={handleInputChange}
								disabled={isLoading}
							/>
							{errors.password && <p className="mt-1 text-sm text-red-600">{errors.password[0]}</p>}
						</div>

						<div>
							<label htmlFor="password_confirmation" className="sr-only">
								Confirm Password
							</label>
							<Input
								id="password_confirmation"
								name="password_confirmation"
								type="password"
								autoComplete="new-password"
								required
								className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
								placeholder="Confirm Password"
								value={formData.password_confirmation}
								onChange={handleInputChange}
								disabled={isLoading}
							/>
							{errors.password_confirmation && <p className="mt-1 text-sm text-red-600">{errors.password_confirmation[0]}</p>}
						</div>
					</div>

					<div>
						<Button type="submit" className="group relative w-full flex justify-center" loading={isLoading} disabled={isLoading}>
							Create Account
						</Button>
					</div>

					<div className="text-center">
						<Link to="/login" className="text-sm hover:text-primary-400">
							Already have an account? Sign in
						</Link>
					</div>
				</form>
			</div>
		</div>
	);
}
